import { useMutation, useQueryClient } from '@tanstack/react-query';
import { track } from '@citibox-panel/analytics';
import type { AccountTypeClass } from '../lib/accountType';

// A courier account's baja also carries the agent's reason; a «Usuario» baja never does.
interface DeleteVars {
  amazonExclusion: boolean;
  reason?: string;
}

// The «Dar de baja» WRITE. retry: false — an irreversible write must never be silently re-sent.
// On success the analytics event fires first (categorical — the exclusion and the account class,
// never the reason — so no PII), then the account query is invalidated AND AWAITED — returning
// the promise keeps the mutation pending until the refetch lands, a deliberate inversion of the
// delivery writes' fire-and-forget. The dialog stays loading
// while the mutation is pending and is dismissed once it settles, and the ficha must never paint
// the pre-baja account after the agent confirmed the baja (UserLayout only skeletons on isPending,
// so an early dismissal would flash the live «Usuario»).
export function useDeleteUser(
  userId: string,
  phoneKey: string,
  deleteUser: (id: string, amazonExclusion: boolean, reason?: string) => Promise<void>,
  accountClass: AccountTypeClass,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ amazonExclusion, reason }: DeleteVars) =>
      reason === undefined
        ? deleteUser(userId, amazonExclusion)
        : deleteUser(userId, amazonExclusion, reason),
    retry: false,
    onSuccess: (_data, { amazonExclusion }) => {
      track('user_deleted', {
        amazon_exclusion: amazonExclusion ? 'true' : 'false',
        account_type: accountClass,
      });
      return queryClient.invalidateQueries({ queryKey: ['phone-search', phoneKey] });
    },
    // The code-agnostic refetch-on-error. Fire-and-forget — never awaited, so the error banner and
    // the filled form stay put — and key-exact with the read: if another agent or the automation
    // already anonymized the account, the refetched ficha resolves it. The sibling writes host this
    // in their layout; here the hook owns both sides of the cycle.
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: ['phone-search', phoneKey] });
    },
  });
}
